import { Alert, AlertDescription, AlertTitle } from '@racefront/shadcn-ui'
import { useEffect, useState, useRef } from 'react'
import toast from 'react-hot-toast'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ApiError } from '../../../types/api-error.type'
import { useAuth } from '../../data/hooks/auth'
import { Sparkle, Loader2, PartyPopper } from 'lucide-react'
import { SignUpData } from '../../../types/user'
import { SignUpForm } from './sign-up-form'
import { RoleSelection } from './role-selection'
import { useLocalization } from '@racefront/common/context/localization-context'
import { useAuthStore } from '../../data/stores/auth.store'
import { RegistrationTokenAcceptance } from './registration-token-acceptance'
import { APP_ROUTES } from '../../../constants/routes'

type UserRole = 'organizer' | 'profile_user'

export default function SignUp() {
  const {
    signUp,
    signUpLoading,
    signUpError,
    signUpSuccess,
    initiateAuthSession,
    initiateAuthData,
    initiateAuthLoading,
  } = useAuth()
  const { locale } = useLocalization()
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const [selectedRole, setSelectedRole] = useState<UserRole>()
  const registrationToken = searchParams.get('registrationToken')
  const { initSession, context, setRedirectUri } = useAuthStore()
  const nav = useNavigate()
  const hasInitiatedAuth = useRef(false)

  const redirectUri = searchParams.get('redirectUri') ?? undefined
  const role = (searchParams.get('role') as UserRole) ?? 'profile_user'

  useEffect(() => {
    if (redirectUri) {
      setRedirectUri(redirectUri)
    }
  }, [redirectUri, setRedirectUri])

  // Combined loading state to track all operations
  const isRegistrationProcessing = signUpLoading || (signUpSuccess && initiateAuthLoading)

  useEffect(() => {
    setSelectedRole(role ?? 'profile_user')
  }, [role])

  useEffect(() => {
    if (signUpError) {
      if ((signUpError as ApiError)?.error === 'user_exists') {
        toast.error(
          intl.formatMessage({ id: 'errors.user_exists_error', defaultMessage: 'Email or phone already in use' }),
        )
      }
    }
  }, [intl, signUpError])

  const handleSubmit = (data: SignUpData) => {
    const payload = {
      ...data,
      locale,
      role: selectedRole ?? 'profile_user',
      ...(registrationToken && { registrationToken }),
    }
    signUp(payload)
  }

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role)
  }

  // Handle successful signup with registrationToken flow
  useEffect(() => {
    if (!signUpSuccess) return
    if (!registrationToken) return
    if (!initSession?.username) return

    initiateAuthSession(initSession.username, undefined, redirectUri)
  }, [signUpSuccess, registrationToken, initSession?.username, initiateAuthSession, redirectUri])

  // Handle successful signup from regular registration flow (only if no registrationToken)
  useEffect(() => {
    if (!signUpSuccess) return
    if (!context?.username) return
    if (hasInitiatedAuth.current) return

    hasInitiatedAuth.current = true
    initiateAuthSession(context.username, undefined, redirectUri)
  }, [signUpSuccess, context?.username, initiateAuthSession, redirectUri])

  // Navigate to verify screen after auth session is initiated (both flows)
  useEffect(() => {
    if ((registrationToken || context?.username) && !initiateAuthLoading && initiateAuthData) {
      nav(APP_ROUTES.Verify)
    }
  }, [registrationToken, initiateAuthLoading, initiateAuthData, nav, context?.username])

  // If no role is selected, show role selection UI
  if (!selectedRole) {
    return <RoleSelection onRoleSelect={handleRoleSelect} />
  }

  return (
    <div>
      <h3 className="text-center text-2xl font-bold">
        <FormattedMessage id={selectedRole === 'organizer' ? 'signup.title_organizer' : 'signup.title'} />
      </h3>
      <h5 className="mt-4 text-center text-lg">
        <FormattedMessage
          id={selectedRole === 'organizer' ? 'signup.subtitle_organizer' : 'signup.subtitle'}
          defaultMessage="Join us to find your next race!"
        />
      </h5>
      <div>
        {(signUpError as ApiError)?.error === 'user_exists' && (
          <Alert className="mt-4" variant="default">
            <Sparkle className="h-4 w-4" />
            <AlertTitle>
              <FormattedMessage id="signup.alert.user_exists_title" defaultMessage="Already a user?" />
            </AlertTitle>
            <AlertDescription className="pt-2">
              <FormattedMessage
                id="signup.alert.user_exists_description"
                defaultMessage="If you already have an account, try to {signInLink}."
                values={{
                  signInLink: (
                    <a
                      className="text-primary font-bold underline"
                      href={APP_ROUTES.SignIn + window.location.search}
                      title="Sign in instead"
                    >
                      <FormattedMessage id="signup.alert.user_exists_link" defaultMessage="sign in instead" />
                    </a>
                  ),
                }}
              />
            </AlertDescription>
          </Alert>
        )}
      </div>
      <div className="mt-8">
        {!signUpSuccess ? (
          registrationToken ? (
            <RegistrationTokenAcceptance onAccept={handleSubmit} isLoading={isRegistrationProcessing} />
          ) : (
            <SignUpForm
              onSubmit={handleSubmit}
              isLoading={isRegistrationProcessing}
              initialRole={selectedRole}
              serverError={signUpError as ApiError}
            />
          )
        ) : initiateAuthLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="text-muted-foreground mt-4 text-center">
              <FormattedMessage id="signup.processing" defaultMessage="Setting up your account..." />
            </p>
          </div>
        ) : (
          <Alert>
            <PartyPopper className="h-4 w-4" />
            <AlertTitle>
              <FormattedMessage id="signup.alert.success_title" defaultMessage="Welcome!" />
            </AlertTitle>
            <AlertDescription className="pt-4">
              <FormattedMessage
                id="signup.alert.success_description"
                defaultMessage="Welcome onboard! In order to proceed  please {signInLink} first!"
                values={{
                  signInLink: (
                    <a
                      className="text-primary font-bold underline"
                      href={APP_ROUTES.SignIn + window.location.search}
                      title="Sign in"
                    >
                      <FormattedMessage id="signup.alert.success_link" defaultMessage="sign in" />
                    </a>
                  ),
                }}
              />
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}
